import { sum } from 'radash';

/**
 * Cart Store
 * ----------------------------
 *
 *
 */
export const useCartStore = defineStore('cart', () => {
    const { analyticsTrackAddToCart, analyticsTrackRemoveFromCart } = useCheckoutAnalytics();

    /**
     * Products
     * ------------------------------------------------------------------------------------------
     */
    const products = ref<Product[]>([]);
    const passengers = ref<Passenger[]>([]);

    const lastProductId = ref<ProductId>();
    const lastProduct = computed(() => products.value.find(p => p.id === lastProductId.value));

    /**
     * Saves a product to the cart
     * ------------------------------------------------------------------------------------------
     * 1. Persists the given product (from an offer flow) into the cart store, replacing any existing product with the same ID
     * 2. Updates the passengers in the cart store, adding any new passengers from the given product
     * 3. Removes any obsolete passengers that are no longer associated with any product in the cart
     *
     * Keeps track of added products for analytics
     */
    function saveProduct(product: Product, newPassengers: Passenger[]) {
        const removedProducts = products.value.filter(p => p.id === product.id);

        // Save to products - replace existing product with same ID (immutable update)
        products.value = [
            ...products.value.filter(p => !removedProducts.some(rp => rp.id === p.id)),
            deepClone(product), // Clone to avoid reference issues back to product store
        ];

        // E-commerce tracking: T3, T6, T8, T35
        for (const product of removedProducts) {
            analyticsTrackRemoveFromCart(product);
        }

        // Update passengers (immutable update)
        passengers.value = [
            ...passengers.value.filter(p => !newPassengers.some(ps => ps.ref === p.ref)),
            ...deepClone(newPassengers), // Clone to avoid reference issues back to passenger store
        ];

        // E-commerce tracking: T2, T5, T7, T34
        analyticsTrackAddToCart(product);

        // Remove passengers that are not longer associated with any product
        removeObsoletePassengers();

        // Update last product ID. Used to be able to go back from cart to "last offer flow"
        const productId = product.isUpsell ? product.upsellParent?.id : product.id;
        lastProductId.value = productId;

        useSessionStore().clearBackLink();
    }

    /**
     * Removes a product from the cart by its ID
     * ------------------------------------------------------------------------------------------
     * 1. Removes the product and any upsell products associated with it from the cart store
     * 2. Updates the passengers in the cart store, removing any passengers no longer associated with any product
     * 3. Keeps track of removed products for analytics
     */
    function removeProduct(id: ProductId) {
        const removedProducts = products.value.filter(p => p.id === id || p.upsellParent?.id === id);

        // Remove products (immutable update)
        products.value = [
            ...products.value
                .filter(p => !removedProducts.some(rp => rp.id === p.id)),
        ];

        // E-commerce tracking: T3, T6, T8, T35
        for (const product of removedProducts) {
            analyticsTrackRemoveFromCart(product);
        }

        // Update passengers (immutable update)
        passengers.value = [
            ...passengers.value.filter(p => products.value.some(prod => prod.passengerRefs?.includes(p.ref))),
        ];
    }

    /**
     * Removes upsell products that depend on the given product ID
     */
    function removeDependingUpsells(id: ProductId) {
        const removedProducts = products.value.filter(p => p.isUpsell && p.upsellParent?.id === id);

        // Remove products (immutable update)
        products.value = [
            ...products.value.filter(p => !removedProducts.some(rp => rp.id === p.id)),
        ];

        // E-commerce tracking: T3, T6, T8, T35
        for (const product of removedProducts) {
            analyticsTrackRemoveFromCart(product);
        }
    }

    /**
     * Removes a passenger from the cart
     */
    function removePassenger(passenger: Passenger) {
        passengers.value = [
            ...passengers.value.filter(p => p.ref !== passenger.ref),
        ];
    }

    /**
     * Removes passengers, that are not part of any cart product
     */
    function removeObsoletePassengers() {
        passengers.value = [
            ...passengers.value.filter(p => products.value.some(prod => prod.passengerRefs.includes(p.ref))),
        ];
    }

    /**
     * Incrementally generates a fresh product ID for a new product based on the given products in the cart.
     * If there are "joch-1", "joch-2" products in the cart, the next generated ID will be "joch-3".
     */
    function getFreshProductId(productConfig: ProductConfig): ProductId {
        const maxNumber = Math.max(
            0,
            ...products.value
                .filter(p => p.config.key === productConfig.key)
                .map(p => Number(p.id.split('-').pop()) || 0),
        );
        return `${productConfig.key}-${maxNumber + 1}`;
    }

    /**
     * Offers (extracted from products)
     * ------------------------------------------------------------------------------------------
     */
    const offers = computed(() => products.value
        .filter(p => !!p.offer)
        .map<Offer>(p => p.offer!) ?? []);

    const allProductsHaveAnOffer = computed(() =>
        products.value.length > 0 && products.value.every(p => Boolean(p.offer)),
    );

    /**
     * Ancillaries
     * ------------------------------------------------------------------------------------------
     */
    const selectedOfferAncillaryRefs = ref<string[]>([]);

    /**
     * Number of cart items (for cart nav)
     * ------------------------------------------------------------------------------------------
     * The number does not equal the number of products, but the number of offer parts / passengers
     * and insurance counts as one item.
     */
    const numberOfCartItems = computed(() =>
        sum(products.value.map(p =>
            p.config.type === 'Accommodation'
                ? (p.offer as OfferAccommodation).offerParts.length || 0
                : p.offer?.passengerRefs.length || 0,
        )) + selectedOfferAncillaryRefs.value.length,
    );

    /**
     * ------------------------------------------------------------------------------------------
     */

    function reset() {
        products.value = [];
        passengers.value = [];
        selectedOfferAncillaryRefs.value = [];
        lastProductId.value = undefined;
    }

    return {
        // Products
        products,
        numberOfCartItems,
        saveProduct,
        removeProduct,
        removeDependingUpsells,
        getFreshProductId,

        // Passengers
        passengers,
        removePassenger,
        removeObsoletePassengers,

        // Offers
        offers,
        allProductsHaveAnOffer,

        // Optional Offers
        selectedOfferAncillaryRefs,

        // Other
        lastProductId,
        lastProduct,

        reset,
    };
}, {
    persist: {
        ...storePersist.storePersistOptions,
        key: 'cart-v6',
    },
});
